import { useState, useEffect } from "react";
import { Phone, MessageCircle, Check, Loader2, ShieldCheck, ClipboardCheck, Calendar as CalendarIcon, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent, getWhatsAppUrl } from "@/lib/tracking";
import { submitLead } from "@/lib/leads.functions";
import { buildLeadAttribution } from "@/lib/lead-attribution";

const services = [
  { value: "mot", label: "MOT Test" },
  { value: "mot-service", label: "MOT + Service" },
  { value: "interim-service", label: "Interim Service" },
  { value: "full-service", label: "Full Service" },
  { value: "major-service", label: "Major Service" },
  { value: "diagnostics", label: "Diagnostics" },
  { value: "repair", label: "Repair" },
];

interface MOTBookingFormProps {
  /** Tracked as the "location" property on every event — useful per-page conversion analysis */
  trackingLocation?: string;
  /** Optional preselected area for area landing pages */
  area?: string;
  className?: string;
}

export function MOTBookingForm({ trackingLocation = "homepage", area, className }: MOTBookingFormProps) {
  const biz = getBusinessConfig();

  const [reg, setReg] = useState("");
  const [service, setService] = useState("mot");
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  // Today's date as min for the date picker — only set after mount to avoid SSR/client mismatch.
  const [today, setToday] = useState<string>("");
  // WhatsApp URL depends on cached client routing — compute after mount only so SSR markup matches first client paint.
  const [waUrl, setWaUrl] = useState<string>("");

  useEffect(() => {
    setToday(new Date().toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    setWaUrl(getWhatsAppUrl(service, { reg, name }));
  }, [service, reg, name]);

  useEffect(() => {
    if (touched) trackEvent("mot_form_start", { location: trackingLocation, ...(area ? { area } : {}) });
    // We only want to fire once per mount when user starts engaging
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched]);

  const valid = name.trim().length >= 2 && phone.trim().length >= 7 && reg.trim().length >= 2;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    setError("");
    trackEvent("mot_form_submit", { location: trackingLocation, service, ...(area ? { area } : {}) });
    try {
      const attribution = buildLeadAttribution({
        formLocation: `mot_form_${trackingLocation}`,
        ctaLabel: "request_callback",
        pageType: area ? "area" : "service",
      });
      await submitLead({
        data: {
          type: "booking" as const,
          service,
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          vehicle_reg: reg.trim(),
          preferred_date: date || undefined,
          notes: area ? `Area: ${area}` : undefined,
          area: area || undefined,
          ...attribution,
        },
      });
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className={`rounded-2xl border border-primary/30 bg-card p-6 md:p-8 text-center shadow-xl ${className || ""}`}>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Check className="h-7 w-7 text-primary" />
        </div>
        <h3 className="mt-4 text-xl font-bold text-foreground">Booking Request Received</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          We'll call you back to confirm a time. Usually within a few hours during workshop hours.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
          <a
            href={biz.phoneHref}
            onClick={() => trackEvent("call_click", { location: `${trackingLocation}_confirmation` })}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Phone className="h-4 w-4" /> Call now {biz.phone}
          </a>
          <a
            href={waUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: `${trackingLocation}_confirmation` })}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-5 py-3 text-sm font-bold text-white hover:bg-[#22c55e] transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      onFocus={() => !touched && setTouched(true)}
      className={`rounded-2xl border border-border bg-card shadow-xl overflow-hidden ${className || ""}`}
    >
      <div className="bg-foreground text-background px-5 md:px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-background/50">Quick Booking</p>
          <h3 className="mt-0.5 text-lg md:text-xl font-bold">Request your MOT or service</h3>
        </div>
        <ShieldCheck className="h-6 w-6 text-primary hidden sm:block" />
      </div>

      <div className="p-5 md:p-6 space-y-4">
        {/* Vehicle reg — UK number-plate style */}
        <div>
          <Label htmlFor="mot-reg" className="text-xs font-semibold uppercase tracking-wider">
            <Car className="h-3 w-3 inline mr-1" />Vehicle registration
          </Label>
          <Input
            id="mot-reg"
            value={reg}
            onChange={(e) => setReg(e.target.value.toUpperCase())}
            placeholder="AB12 CDE"
            required
            maxLength={10}
            className="mt-1.5 h-12 text-center font-extrabold tracking-[0.2em] text-lg uppercase bg-[#FFD307] text-black border-2 border-[#FFD307] placeholder:text-black/70 placeholder:font-normal placeholder:tracking-normal placeholder:text-sm focus-visible:ring-primary"
          />
        </div>

        {/* Service + date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="mot-service" className="text-xs font-semibold uppercase tracking-wider">
              <ClipboardCheck className="h-3 w-3 inline mr-1" />Service
            </Label>
            <select
              id="mot-service"
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {services.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="mot-date" className="text-xs font-semibold uppercase tracking-wider">
              <CalendarIcon className="h-3 w-3 inline mr-1" />Preferred date
            </Label>
            <Input
              id="mot-date"
              type="date"
              value={date}
              min={today}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1.5 h-11"
            />
          </div>
        </div>

        {/* Contact details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="mot-name" className="text-xs font-semibold uppercase tracking-wider">Your name *</Label>
            <Input
              id="mot-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              required
              minLength={2}
              maxLength={100}
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label htmlFor="mot-phone" className="text-xs font-semibold uppercase tracking-wider">Phone *</Label>
            <Input
              id="mot-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07…"
              required
              minLength={7}
              maxLength={20}
              className="mt-1.5 h-11"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="mot-email" className="text-xs font-semibold uppercase tracking-wider">Email <span className="text-muted-foreground normal-case font-normal">(optional)</span></Label>
          <Input
            id="mot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            maxLength={255}
            className="mt-1.5 h-11"
          />
        </div>

        {error && <p className="text-sm text-destructive font-medium">{error}</p>}

        <Button type="submit" disabled={!valid || submitting} className="w-full h-12 text-base font-bold">
          {submitting ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Sending request…</>
          ) : (
            <>Request a Callback</>
          )}
        </Button>

        <div className="grid grid-cols-2 gap-2 pt-1">
          <a
            href={biz.phoneHref}
            onClick={() => trackEvent("call_click", { location: `${trackingLocation}_form` })}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2.5 text-xs font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors"
          >
            <Phone className="h-3.5 w-3.5" /> Call instead
          </a>
          <a
            href={waUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: `${trackingLocation}_form` })}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[#25D366]/40 bg-[#25D366]/5 px-3 py-2.5 text-xs font-bold text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
        </div>

        <p className="text-[11px] text-center text-muted-foreground">
          We'll only use your details to confirm your booking. No spam, ever.
        </p>
      </div>
    </form>
  );
}
